import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getApolloQuota from "../http/getApolloQuota";
import getLeadActivePeopleOps from "../http/getLeadActivePeopleOps";
import getPeopleDiscoveryTrace from "../http/getPeopleDiscoveryTrace";
import getPeopleRankingPrompt from "../http/getPeopleRankingPrompt";
import getPeopleRankingPromptDiff from "../http/getPeopleRankingPromptDiff";
import patchLeadPeopleSelection from "../http/patchLeadPeopleSelection";
import {
  deletePeoplePromptRule,
  reorderPeoplePromptRules,
  restorePeoplePromptRevision,
  savePeoplePromptAddendum,
  savePeoplePromptPreamble,
  savePeoplePromptRule,
} from "../http/peopleRankingPromptOps";
import postLeadFindPeople, { postVoaFindPeople } from "../http/postFindPeople";
import postPeopleRankingPromptTest, {
  PeopleRankingPromptTestRequest,
} from "../http/postPeopleRankingPromptTest";
import postLeadPullContactInfo from "../http/postPullContactInfo";
import { PeopleRole } from "../models/peopleCandidate";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

// Step-5 people-discovery mutations. Mirrors the landlord-research mutations in
// useLeads: the find-people mutations only kick off the LRO (the caller's poll
// effect invalidates the lead on completion via useOperation); the selection save
// invalidates the lead. The editable-prompt hooks land with the prompt card.
export const usePeopleDiscovery = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const findLeadPeople = useMutation(
    async ({
      leadId,
      role,
      domainOverride,
    }: {
      leadId: string;
      role: PeopleRole;
      domainOverride?: string;
    }) =>
      postLeadFindPeople(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        role,
        domainOverride
      )
  );

  const findVoaPeople = useMutation(
    async ({
      sourceRef,
      role,
      domainOverride,
    }: {
      sourceRef: string;
      role: PeopleRole;
      domainOverride?: string;
    }) =>
      postVoaFindPeople(
        await getAuthTokenBlocking(getToken, relogin),
        sourceRef,
        role,
        domainOverride
      )
  );

  // Kick off step-7 contact enrichment for the operator's selected people (one action
  // per role) as an LRO. Returns {operationKey, siteId}; the caller polls
  // useOperation(...) and, on completion, invalidates the lead so the pulled contacts
  // appear. No onSuccess invalidation here - the poll effect owns that (mirrors
  // findLeadPeople), so the open Contacts panel doesn't remount mid-pull.
  const pullContactInfo = useMutation(
    async ({
      leadId,
      role,
      apolloIds,
    }: {
      leadId: string;
      role: PeopleRole;
      apolloIds: string[];
    }) =>
      postLeadPullContactInfo(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        role,
        apolloIds
      )
  );

  const savePeopleSelection = useMutation(
    async ({
      leadId,
      role,
      selectedApolloIds,
    }: {
      leadId: string;
      role: PeopleRole;
      selectedApolloIds: string[];
    }) =>
      patchLeadPeopleSelection(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        role,
        selectedApolloIds
      ),
    {
      onSuccess: (_data, { leadId }) => {
        queryClient.invalidateQueries(["lead", leadId]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  // The editable people-ranking prompt is per-role (one "landlord" prompt, one
  // "tenant" prompt), a rule set (a preamble plus addressable rules). Each write
  // appends one revision; every mutation invalidates that role's prompt query so
  // the card re-renders (and its baseRevision advances) from the fresh active
  // revision. All are gated on `baseRevision` optimistic concurrency, so a stale
  // edit 409s rather than clobbering another.
  const invalidatePeoplePrompt = {
    onSuccess: (_data: unknown, variables: { role: PeopleRole }) => {
      queryClient.invalidateQueries(["peopleRankingPrompt", variables.role]);
    },
  };
  const savePeoplePromptRuleMut = useMutation(
    async (body: Parameters<typeof savePeoplePromptRule>[1]) =>
      savePeoplePromptRule(await getAuthTokenBlocking(getToken, relogin), body),
    invalidatePeoplePrompt
  );
  const deletePeoplePromptRuleMut = useMutation(
    async (body: Parameters<typeof deletePeoplePromptRule>[1]) =>
      deletePeoplePromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePeoplePrompt
  );
  const reorderPeoplePromptRulesMut = useMutation(
    async (body: Parameters<typeof reorderPeoplePromptRules>[1]) =>
      reorderPeoplePromptRules(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePeoplePrompt
  );
  const savePeoplePromptPreambleMut = useMutation(
    async (body: Parameters<typeof savePeoplePromptPreamble>[1]) =>
      savePeoplePromptPreamble(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePeoplePrompt
  );
  const savePeoplePromptAddendumMut = useMutation(
    async (body: Parameters<typeof savePeoplePromptAddendum>[1]) =>
      savePeoplePromptAddendum(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePeoplePrompt
  );
  const restorePeoplePromptRevisionMut = useMutation(
    async (body: Parameters<typeof restorePeoplePromptRevision>[1]) =>
      restorePeoplePromptRevision(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePeoplePrompt
  );

  // Kick off a test people-discovery run against a DRAFT prompt (not saved) as an
  // LRO. Returns {operationKey, siteId}; the caller polls useOperation(...) and
  // reads candidates + trace ref from the operation result. Persists nothing.
  const testPeopleRankingPrompt = useMutation(
    async (body: PeopleRankingPromptTestRequest) =>
      postPeopleRankingPromptTest(
        await getAuthTokenBlocking(getToken, relogin),
        body
      )
  );

  return {
    findLeadPeople,
    findVoaPeople,
    pullContactInfo,
    savePeopleSelection,
    savePeoplePromptRuleMut,
    deletePeoplePromptRuleMut,
    reorderPeoplePromptRulesMut,
    savePeoplePromptPreambleMut,
    savePeoplePromptAddendumMut,
    restorePeoplePromptRevisionMut,
    testPeopleRankingPrompt,
  };
};

// The stored AI trace for a people-discovery run. `subjectRef` is the composed
// storage ref ("<leadId>:<role>" for a real run, the result's traceSubjectRef for
// a test run).
export const usePeopleDiscoveryTrace = (
  subjectRef: string | null,
  runId: string | null,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["peopleDiscoveryTrace", subjectRef, runId],
    async () =>
      getPeopleDiscoveryTrace(
        await getAuthTokenBlocking(getToken, relogin),
        subjectRef!,
        runId!
      ),
    // runId is read off the persisted role blob and is required by the endpoint -
    // only fire once it exists (no "latest for this subject" fallback).
    { enabled: !!subjectRef && !!runId && enabled }
  );
};

// The live discovery + enrichment op keys for a (lead, role), so the Contacts panel can
// re-attach its progress poll on mount: a run started before the operator navigated away
// keeps going server-side, and this reconnects the remounted panel to it (the way landlord
// research survives navigation) instead of painting "stopped". Read-only; fires on mount
// (staleTime 0 + refetchOnMount) so a remount always reconnects. `enabled` gates it to a
// rendered cell with a chosen company.
export const useLeadActivePeopleOps = (
  leadId: string,
  role: PeopleRole,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadActivePeopleOps", leadId, role],
    async () =>
      getLeadActivePeopleOps(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        role
      ),
    { enabled, staleTime: 0, refetchOnMount: "always" }
  );
};

// Live Apollo free-tier daily quota (calls used vs limit) for the Leads-costs card.
export const useApolloQuota = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["apolloQuota"],
    async () => getApolloQuota(await getAuthTokenBlocking(getToken, relogin)),
    // "Apollo discovery today" is a per-day counter that resets at 00:00 UTC. It must
    // never be served from cache across that boundary, or a tab left open overnight
    // shows yesterday's pre-reset total until something invalidates the query. Always
    // re-read on mount / focus so the operator sees the live count.
    {
      enabled,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    }
  );
};

// The editable people-ranking prompt for one role: active revision + full history
// + the non-editable enforced footer. Admin-only (inrange-org gated). The prompt
// is per-role, so the query is keyed on the role.
export const usePeopleRankingPrompt = (role: PeopleRole, enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["peopleRankingPrompt", role],
    async () =>
      getPeopleRankingPrompt(
        await getAuthTokenBlocking(getToken, relogin),
        role
      ),
    { enabled }
  );
};

// Rule-level + text diff between two revisions of a role's people-ranking prompt.
// Admin-only.
export const usePeopleRankingPromptDiff = (
  role: PeopleRole,
  fromRevision: number | null,
  toRevision: number | null,
  enabled = true
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["peopleRankingPromptDiff", role, fromRevision, toRevision],
    async () =>
      getPeopleRankingPromptDiff(
        await getAuthTokenBlocking(getToken, relogin),
        role,
        fromRevision!,
        toRevision!
      ),
    { enabled: enabled && fromRevision !== null && toRevision !== null }
  );
};

export default usePeopleDiscovery;
